import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { ITask, ITaskParameter, ITasksCountData, ITasksStatus } from '@fi-shared/fi-task-shared/fi-task-shared';
import { AccessRightFilterCollectionModel } from '@fi-shared/models';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { Subject } from 'rxjs';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ActionType } from '@fi-shared/types';
import { FiTaskHelperService } from '@fi-shared/fi-task-shared/fi-task-helper.service';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiTaskAssignmentType, FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';
import { ITaskV2 } from '@fi-app/task-v2/fi-task-v2';

@Injectable()
export class FiTaskSharedService {
    constructor(
        private fiBackend: FiBackendService,
        private userDataService: FiUserDataService,
        private snackBar: FiSnackBarService,
        private appTextService: AppTextService,
        private fiLoading: LoadingService,
        private taskHelperService: FiTaskHelperService,
    ) {}

    private _taskSaved$ = new Subject<ITask>();
    get taskSaved$() {
        return this._taskSaved$.asObservable();
    }

    private _TasksUpdatedInternal$ = new Subject<ITask[]>();
    get TasksUpdatedInternal$() {
        return this._TasksUpdatedInternal$.asObservable();
    }

    getTasksListData(parameter: ITaskParameter) {
        const promise = this.fiBackend.fetchDataPost<IItemsListDataFetchResult<ITask>>(
            'Task/Task/GetTasksListData',
            parameter,
        );
        return promise;
    }

    getTasksCountForTabs(parameter: ITaskParameter, isTaskV2: boolean): Promise<ITasksCountData[]> {
        const url = isTaskV2 ? `Task/Task2/GetTasksCountForTabs` : `Task/Task/GetTasksCountForTabs`;
        const promise = this.fiBackend.fetchDataPost<ITasksCountData[]>(url, parameter);
        return promise;
    }

    // obsolete
    getAssignedTasks(completedFilter?: boolean) {
        const completedFilterUrlParameter =
            completedFilter === true || completedFilter === false ? completedFilter : ''; // we don't want pass undefined to backend
        const promise = this.fiBackend.fetchData<ITask[]>(
            `Task/Task/GetAssignedTasks?completedFilter=${completedFilterUrlParameter}`,
        );
        promise.then((tasks) => this._TasksUpdatedInternal$.next(tasks));
        return promise;
    }

    getArchivedTasks() {
        return this.fiBackend.fetchData<ITask[]>(`Task/Task/GetArchivedTasks`);
    }

    getTask(taskId: number) {
        return this.fiBackend.fetchData<ITask>(`Task/Task/GetTask/${taskId}`);
    }

    getTasksByIds(taskIds: number[], isTaskV2: boolean) {
        const url = isTaskV2 ? `Task/Task2/GetTasksByIds` : `Task/Task/GetTasksByIds`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<ITaskV2[]>(url, taskIds));
    }

    getNewTaskInstance(args?: { userId?: number; taskType?: FiTaskType; checkpointAnswerId?: number }): ITask {
        if (!args) {
            args = {};
        }
        const selectedUserId: number = args?.userId || this.userDataService.getUserId();
        const task: ITask = {
            TaskType: args.taskType ?? FiTaskType.Common,
            MediaBankFileReferences: [],
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            CommentsCount: 0,
            AssignmentType:
                selectedUserId === this.userDataService.getUserId()
                    ? FiTaskAssignmentType.Private
                    : FiTaskAssignmentType.Users,
        };
        task.AccessRightFilterCollection.AccessRightUserFilter.UserIds.push(selectedUserId);
        if (args.taskType === FiTaskType.ChecklistOneTime) {
            task.CheckpointAnswerId = args.checkpointAnswerId;
        }
        return task;
    }

    saveTask(task: ITask, isTaskV2: boolean) {
        const url = isTaskV2 ? `Task/Task2/SaveTask` : `Task/Task/SaveTask`;
        const promise = this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<number>(url, task));
        promise.then((newTaskId) => {
            if (!task.Id) {
                task.Id = newTaskId;
                task.CreatedByUserId = this.userDataService.getUserId();
            }
            this._taskSaved$.next(task);
        });
        return promise;
    }

    getTasksStatus() {
        return this.fiBackend.fetchData<ITasksStatus>(`Task/Task/GetTasksStatus`);
    }

    updateTaskCompletedStatus(taskIds: number[], isCompleted: boolean): Promise<void> {
        return this.fiBackend.fetchDataPost<void>(`Task/Task/SaveTaskCompleted`, {
            TaskIds: taskIds,
            IsCompleted: isCompleted,
        });
    }

    saveTaskCompleted(
        tasks: ITask[],
        isCompleted: boolean,
        callback: () => void,
        errorCallback: () => void,
        showSnackbar?: boolean,
    ) {
        const tasksCopy = tasks.slice(0);
        this.fiLoading.trackPromise(
            this.updateTaskCompletedStatus(
                tasks.map((task) => task.Id),
                isCompleted,
            ).then(
                () => {
                    const currentUser = this.userDataService.userData;
                    tasks.forEach((task) => {
                        if (isCompleted) {
                            task.IsCompleted = true;
                            task.CompletedByUserId = currentUser.UserId;
                            task.CompletedByUserName = currentUser.FullName;
                            task.CompletedOn = new Date().toISOString();
                            task.CompletionTime = 0;
                        } else {
                            task.IsCompleted = false;
                            task.CompletedByUserId = null;
                            task.CompletedByUserName = null;
                            task.CompletedOn = null;
                            task.CompletionTime = null;
                        }
                    });
                    this._TasksUpdatedInternal$.next(tasks);
                    if (showSnackbar) {
                        const snackbarMessageId = (() => {
                            if (isCompleted) {
                                return tasksCopy.length === 1 ? 'task.task_completed' : 'task.tasks_completed';
                            }
                            return 'task.set_not_complete';
                        })();
                        this.snackBar
                            .showSnackbar({
                                textMessage: this.appTextService.get(snackbarMessageId),
                                buttonText: this.appTextService.get('common.undo'),
                            })
                            .onAction()
                            .subscribe(() => {
                                this.saveTaskCompleted(tasksCopy, !isCompleted, callback, errorCallback);
                            });
                    }
                    callback();
                },
                () => {
                    errorCallback();
                },
            ),
        );
    }

    saveTaskArchived(tasks: ITask[], isArchived: boolean) {
        return this.fiBackend
            .fetchDataPost<void>(`Task/Task/SaveTaskArchived`, {
                TaskIds: tasks.map((task) => task.Id),
                IsArchived: isArchived,
            })
            .then(() => {
                tasks.forEach((task) => {
                    if (isArchived) {
                        task.IsArchived = true;
                        task.ArchivedOn = new Date().toISOString();
                    } else {
                        task.IsArchived = false;
                        task.ArchivedOn = null;
                    }
                });
                this._TasksUpdatedInternal$.next(tasks);
            });
    }

    deleteTasks(taskIds: number[]) {
        return this.fiBackend.fetchDataPost<void>(`Task/Task/DeleteTasks`, taskIds);
    }

    sendTaskReminders(taskIds: number[]) {
        return this.fiBackend.fetchDataPost<void>(`Task/Task/SendTaskReminders`, taskIds);
    }

    changeTaskAssignees(taskId: number, userIds: number[], actionType: ActionType) {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<void>(`Task/Task/ChangeTaskAssignees`, {
                taskId: taskId,
                userIds: userIds,
                actionType: actionType,
            }),
        );
    }
}
